import React from 'react';
import _ from 'lodash';

import {
  ApprovalTypeName,
  AuditLogWidgetDocument,
  Partner_ReturnAuthorizationActionsWidgetDocument,
  Partner_ReturnAuthorizationLifeCycleWidgetDocument,
  SingleOrderRecordNavigatorDocument,
  useAcceptReturnAuthorizationMutation,
  useApproveReturnAuthorizationMutation,
  useDeleteReturnAuthorizationMutation,
  useGenerateReturnLabelMutation,
  useInitiateRefundMutation,
  usePartner_ReturnAuthorizationActionsWidgetQuery,
  useReverseReturnAuthorizationAcceptanceDecisionMutation,
  useReverseReturnAuthorizationApprovalDecisionMutation,
  useReverseReturnAuthorizationRefundDenialDecisionMutation
} from 'generated/graphql';



export interface IData {
  returnLabelUrl: string;
  canGenerateLabel: boolean;
  approvalStatus: ApprovalTypeName;
  hasItemReceipt: boolean;
  hasUnreceivedItems: boolean;
  acceptanceStatus: ApprovalTypeName;
  refundStatus: ApprovalTypeName;
  orderId: string;
  canDeleteReturnAuthorization: boolean;
}

export function useQuery(returnAuthorizationId: string) {
  const tuple = usePartner_ReturnAuthorizationActionsWidgetQuery({
    variables: {
      returnAuthorizationId
    }
  });

  return React.useMemo(
    () => {
      const returnAuthorization = tuple.data?.returnAuthorization;
      const acceptance = returnAuthorization?.acceptance;
      const approval = returnAuthorization?.approval;
      const data: IData = {
        canDeleteReturnAuthorization: tuple.data?.canDeleteReturnAuthorization ?? false,
        canGenerateLabel: returnAuthorization?.store.type.name !== 'telgoo5',
        approvalStatus: approval?.approvalType.name ?? 'pending',
        hasItemReceipt: !_.isNil(returnAuthorization?.itemReceipt),
        acceptanceStatus: acceptance?.approvalType.name ?? 'pending',
        refundStatus: returnAuthorization?.refund?.approvalType.name ?? 'pending',
        hasUnreceivedItems: !_.isNil(returnAuthorization?.itemReceipt),
        returnLabelUrl: returnAuthorization?.shipment?.labelUrl ?? '',
        orderId: returnAuthorization?.order.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useAcceptReturnAuthorization(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useAcceptReturnAuthorizationMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    (approvalTypeName: ApprovalTypeName) => {
      fnMutate({
        variables: {
          returnAuthorizationId,
          approvalTypeName
        }
      });
    },
    [fnMutate, returnAuthorizationId]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useInitiateRefund(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useInitiateRefundMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    (approvalTypeName: ApprovalTypeName) => {
      fnMutate({
        variables: {
          input: {
            returnAuthorizationId,
            approvalTypeName
          }
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useGenerateReturnLabel(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useGenerateReturnLabelMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          input: {
            returnAuthorizationId
          }
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useApproveReturnAuthorization(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useApproveReturnAuthorizationMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    (approvalTypeName: ApprovalTypeName) => {
      fnMutate({
        variables: {
          approvalTypeName,
          returnAuthorizationId
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useReverseReturnAuthorizationApprovalDecision(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useReverseReturnAuthorizationApprovalDecisionMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          returnAuthorizationId
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useReverseReturnAuthorizationAcceptanceDecision(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useReverseReturnAuthorizationAcceptanceDecisionMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          returnAuthorizationId
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useReverseReturnAuthorizationRefundDenialDecision(returnAuthorizationId: string) {
  const [fnMutate, tuple] = useReverseReturnAuthorizationRefundDenialDecisionMutation({
    awaitRefetchQueries: true,
    refetchQueries: __genRefetchQueries(returnAuthorizationId)
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          returnAuthorizationId
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __genRefetchQueries(returnAuthorizationId: string) {
  return [
    {
      query: Partner_ReturnAuthorizationLifeCycleWidgetDocument,
      variables: {
        returnAuthorizationId,
        queryAsCustomer: false
      }
    },
    {
      query: Partner_ReturnAuthorizationActionsWidgetDocument,
      variables: { returnAuthorizationId }
    },
    {
      query: AuditLogWidgetDocument,
      variables: {
        id: returnAuthorizationId,
        isQueryingOrder: false
      }
    }
  ];
}



export function useDeleteReturnAuthorization(returnAuthorizationId: string, orderId: string) {
  const [fnMutate, tuple] = useDeleteReturnAuthorizationMutation({
    refetchQueries: [
      {
        query: SingleOrderRecordNavigatorDocument,
        variables: { orderId }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          returnAuthorizationId
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
